import React, { MouseEvent, useEffect, useMemo, useState } from 'react';
import { useMutation, useSubscription } from '@apollo/client';
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { CancelRounded, CheckCircleRounded, RemoveCircleOutlineRounded } from '@material-ui/icons';
import { alpha, makeStyles } from '@material-ui/core/styles';
import { listPreOpBoardPatients, setPreOpBoardStatus } from '@/graph/patients';
import Troubleshooting from '@/components/pages/kiosk/tablet/Troubleshooting';
import {
  groupPreOpBoardPatients,
  isReadyForOr,
  PreOpBoardPatient,
  PreOpBoardSort,
  PreOpBoardStatusField,
} from './preOpBoardModel';

type StatusValue = boolean | null;

type OptimisticStatus = {
  value: StatusValue;
  base: StatusValue;
};

type StatusMenuState = {
  anchor: HTMLElement;
  patientId: string | number;
  field: PreOpBoardStatusField;
} | null;

const graphQlStatusField: Record<PreOpBoardStatusField, string> = {
  preOpBoardAnesthesiaReady: 'ANESTHESIA_READY',
  preOpBoardSurgeonReady: 'SURGEON_READY',
  preOpBoardHistoryAndPhysicalReady: 'HISTORY_AND_PHYSICAL_READY',
};

const statusFieldLabel: Record<PreOpBoardStatusField, string> = {
  preOpBoardAnesthesiaReady: 'A-Ready',
  preOpBoardSurgeonReady: 'S-Ready',
  preOpBoardHistoryAndPhysicalReady: 'H&P',
};

const useStyles = makeStyles(theme => ({
  root: {
    height: '100vh',
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    backgroundColor: theme.palette.background.default,
    color: theme.palette.text.primary,
  },
  header: {
    padding: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    flexShrink: 0,
    backgroundColor: theme.palette.background.default,
    borderBottom: `1px solid ${theme.palette.divider}`,
    zIndex: 3,
  },
  title: {
    fontWeight: 400,
  },
  total: {
    color: theme.palette.text.secondary,
    fontWeight: 500,
    whiteSpace: 'nowrap',
    '& strong': {
      color: theme.palette.text.primary,
      fontWeight: 600,
    },
  },
  tableContainer: {
    flex: 1,
    minHeight: 0,
    overflow: 'auto',
    margin: theme.spacing(1.5),
    width: `calc(100% - ${theme.spacing(3)}px)`,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
  },
  footer: {
    flexShrink: 0,
    borderTop: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.default,
    padding: theme.spacing(2),
  },
  table: {
    tableLayout: 'fixed',
    minWidth: 970,
  },
  headerCell: {
    backgroundColor: theme.palette.grey.A100,
    color: theme.palette.text.primary,
    borderBottom: `2px solid ${theme.palette.divider}`,
    fontSize: 'clamp(0.7rem, 1vw, 0.9rem)',
    fontWeight: 700,
    lineHeight: 1.15,
    padding: theme.spacing(1.25, 0.75),
    whiteSpace: 'nowrap',
  },
  sortLabel: {
    color: `${theme.palette.text.secondary} !important`,
    '&.MuiTableSortLabel-active': {
      color: `${theme.palette.primary.main} !important`,
    },
    '& .MuiTableSortLabel-icon': {
      color: `${theme.palette.primary.main} !important`,
    },
  },
  groupCell: {
    padding: theme.spacing(0.8, 1.25),
    backgroundColor: alpha(theme.palette.primary.main, 0.16),
    borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.35)}`,
    borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
  },
  groupName: {
    color: theme.palette.primary.main,
    fontWeight: 700,
    letterSpacing: '0.01em',
  },
  groupCount: {
    marginLeft: theme.spacing(1),
    color: theme.palette.text.secondary,
    fontWeight: 500,
  },
  cell: {
    padding: theme.spacing(0.75),
    height: 56,
    fontSize: 'clamp(0.7rem, 1vw, 0.92rem)',
    borderBottom: `1px solid ${theme.palette.divider}`,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  surgeon: { width: '11%' },
  order: { width: '7%' },
  time: { width: '7%' },
  patient: { width: '13%' },
  bay: { width: '5%' },
  nurse: { width: '10%' },
  mda: { width: '10%' },
  anesthesia: { width: '8%' },
  status: { width: '5%' },
  event: { width: '14%' },
  center: {
    textAlign: 'center',
  },
  statusButton: {
    width: 46,
    height: 46,
    padding: 0,
    color: theme.palette.text.disabled,
    '&:disabled': {
      color: theme.palette.text.disabled,
    },
  },
  statusReady: {
    color: theme.palette.success.main,
  },
  statusNotReady: {
    color: theme.palette.error.main,
  },
  blankStatus: {
    fontSize: '1.2rem',
    fontWeight: 500,
  },
  menuItem: {
    minHeight: 52,
    minWidth: 190,
    gap: theme.spacing(1.25),
  },
  readyMenuIcon: { color: theme.palette.success.main },
  notReadyMenuIcon: { color: theme.palette.error.main },
  clearMenuIcon: { color: theme.palette.text.secondary },
  eventReady: {
    backgroundColor: alpha(theme.palette.success.main, 0.2),
    color: theme.palette.success.light,
    border: `1px solid ${alpha(theme.palette.success.main, 0.35)}`,
    fontWeight: 700,
  },
  eventPreOp: {
    backgroundColor: theme.palette.action.hover,
    color: theme.palette.text.secondary,
    border: `1px solid ${theme.palette.divider}`,
    fontWeight: 600,
  },
  state: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    gap: theme.spacing(2),
    color: theme.palette.text.secondary,
  },
  '@media (max-width: 1100px)': {
    header: {
      padding: theme.spacing(1.5),
    },
    tableContainer: {
      margin: theme.spacing(1),
      width: `calc(100% - ${theme.spacing(2)}px)`,
    },
    headerCell: {
      padding: theme.spacing(1, 0.5),
    },
    cell: {
      padding: theme.spacing(0.5),
      height: 50,
    },
    footer: {
      padding: theme.spacing(1.5),
    },
  },
}));

const fullValue = (value?: string | null) => value?.trim() || '';

const TextCell = ({ value, className }: { value?: string | null; className?: string }) => {
  const text = fullValue(value);
  return (
    <Tooltip title={text} disableHoverListener={!text} enterTouchDelay={300}>
      <TableCell className={className} tabIndex={text ? 0 : undefined}>
        {text}
      </TableCell>
    </Tooltip>
  );
};

const StatusIcon = ({ value, classes }: { value: StatusValue; classes: ReturnType<typeof useStyles> }) => {
  if (value === true) return <CheckCircleRounded className={classes.statusReady} fontSize="large" />;
  if (value === false) return <CancelRounded className={classes.statusNotReady} fontSize="large" />;
  return <span className={classes.blankStatus}>—</span>;
};

const statusValueLabel = (value: StatusValue) => {
  if (value === true) return 'ready';
  if (value === false) return 'not ready';
  return 'blank';
};

const PreOpBoard = () => {
  const classes = useStyles();
  const { data, loading, error } = useSubscription<{ patients: PreOpBoardPatient[] }>(listPreOpBoardPatients, {
    shouldResubscribe: true,
  });
  const [writeStatus] = useMutation(setPreOpBoardStatus);
  const [sort, setSort] = useState<PreOpBoardSort>({ field: 'caseOrder', direction: 'asc' });
  const [menu, setMenu] = useState<StatusMenuState>(null);
  const [lastConfirmedPatients, setLastConfirmedPatients] = useState<PreOpBoardPatient[]>([]);
  const [hasConfirmedData, setHasConfirmedData] = useState(false);
  const [optimistic, setOptimistic] = useState<Record<string, OptimisticStatus>>({});
  const [pending, setPending] = useState<Record<string, boolean>>({});
  const [failure, setFailure] = useState<string | null>(null);

  useEffect(() => {
    if (data?.patients !== undefined) {
      setLastConfirmedPatients(data.patients);
      setHasConfirmedData(true);
    }
  }, [data?.patients]);

  const patients = data?.patients ?? lastConfirmedPatients;
  const hasAuthoritativeData = data?.patients !== undefined || hasConfirmedData;
  const groups = useMemo(() => groupPreOpBoardPatients(patients, sort), [patients, sort]);

  useEffect(() => {
    setOptimistic(current => {
      let changed = false;
      const next = { ...current };
      Object.entries(current).forEach(([key, optimisticStatus]) => {
        const separator = key.indexOf(':');
        const patientId = key.slice(0, separator);
        const field = key.slice(separator + 1) as PreOpBoardStatusField;
        const patient = patients.find(item => String(item.id) === patientId);
        if (!patient || (patient[field] ?? null) !== optimisticStatus.base) {
          delete next[key];
          changed = true;
        }
      });
      return changed ? next : current;
    });
  }, [patients]);

  const toggleSort = (field: PreOpBoardSort['field']) => {
    setSort(current => ({
      field,
      direction: current.field === field && current.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const openStatusMenu = (event: MouseEvent<HTMLElement>, patientId: string | number, field: PreOpBoardStatusField) => {
    setMenu({ anchor: event.currentTarget, patientId, field });
  };

  const updateStatus = async (value: StatusValue) => {
    if (!menu) return;
    const { patientId, field } = menu;
    const pendingKey = `${patientId}:${field}`;
    const patient = patients.find(item => String(item.id) === String(patientId));
    const base = patient?.[field] ?? null;
    setMenu(null);

    if (value === base) return;

    setOptimistic(current => ({ ...current, [pendingKey]: { value, base } }));
    setPending(current => ({ ...current, [pendingKey]: true }));
    try {
      const result = await writeStatus({
        variables: { patientId, field: graphQlStatusField[field], value },
      });
      if (!result.data?.success) throw new Error('The status could not be saved.');
      setPending(current => {
        const next = { ...current };
        delete next[pendingKey];
        return next;
      });
    } catch (_error) {
      setPending(current => {
        const next = { ...current };
        delete next[pendingKey];
        return next;
      });
      setOptimistic(current => {
        const next = { ...current };
        delete next[pendingKey];
        return next;
      });
      setFailure('Status was not saved. Tap the cell to try again.');
    }
  };

  const renderStatusCell = (patient: PreOpBoardPatient, field: PreOpBoardStatusField) => {
    const pendingKey = `${patient.id}:${field}`;
    const isPending = Boolean(pending[pendingKey]);
    const value = Object.prototype.hasOwnProperty.call(optimistic, pendingKey)
      ? optimistic[pendingKey].value
      : (patient[field] ?? null);
    const label = statusFieldLabel[field];
    return (
      <TableCell className={`${classes.cell} ${classes.center}`}>
        <IconButton
          className={classes.statusButton}
          onClick={event => openStatusMenu(event, patient.id, field)}
          disabled={isPending}
          aria-label={`${label} for ${patient.name}: ${isPending ? 'saving' : statusValueLabel(value)}`}
          aria-haspopup="menu"
          aria-expanded={menu?.patientId === patient.id && menu?.field === field}
        >
          {isPending ? <CircularProgress size={28} /> : <StatusIcon value={value} classes={classes} />}
        </IconButton>
      </TableCell>
    );
  };

  if (loading && !hasAuthoritativeData) {
    return (
      <Box className={classes.root}>
        <Box className={classes.state}>
          <CircularProgress size={48} />
          <Typography>Loading PreOP Board…</Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box className={classes.root}>
      <Box className={classes.header}>
        <Typography variant="h1" className={classes.title}>
          PreOP Board
        </Typography>
        <Typography variant="h6" className={classes.total}>
          Total Cases: <strong>{patients.length}</strong>
        </Typography>
      </Box>

      {error && (
        <Alert severity="warning">Connection interrupted. Showing the last confirmed board while reconnecting.</Alert>
      )}

      {!hasAuthoritativeData ? (
        <Box className={classes.state}>
          <Typography variant="h6">The PreOP Board could not be loaded. Reconnecting…</Typography>
        </Box>
      ) : patients.length === 0 ? (
        <Box className={classes.state}>
          <Typography variant="h6">No patients are currently in Pre-Op.</Typography>
        </Box>
      ) : (
        <TableContainer component={Paper} elevation={0} className={classes.tableContainer}>
          <Table stickyHeader size="small" className={classes.table} aria-label="PreOP Board">
            <TableHead>
              <TableRow>
                <TableCell className={`${classes.headerCell} ${classes.surgeon}`}>Surgeon</TableCell>
                <TableCell className={`${classes.headerCell} ${classes.order} ${classes.center}`}>
                  <TableSortLabel
                    active={sort.field === 'caseOrder'}
                    direction={sort.field === 'caseOrder' ? sort.direction : 'asc'}
                    onClick={() => toggleSort('caseOrder')}
                    className={classes.sortLabel}
                  >
                    Case Order
                  </TableSortLabel>
                </TableCell>
                <TableCell className={`${classes.headerCell} ${classes.time} ${classes.center}`}>
                  <TableSortLabel
                    active={sort.field === 'time'}
                    direction={sort.field === 'time' ? sort.direction : 'asc'}
                    onClick={() => toggleSort('time')}
                    className={classes.sortLabel}
                  >
                    Time
                  </TableSortLabel>
                </TableCell>
                <TableCell className={`${classes.headerCell} ${classes.patient}`}>Name</TableCell>
                <TableCell className={`${classes.headerCell} ${classes.bay} ${classes.center}`}>Bay</TableCell>
                <TableCell className={`${classes.headerCell} ${classes.nurse}`}>Pre-OP RN</TableCell>
                <TableCell className={`${classes.headerCell} ${classes.mda}`}>MDA</TableCell>
                <TableCell className={`${classes.headerCell} ${classes.anesthesia}`}>Anes Type</TableCell>
                <TableCell className={`${classes.headerCell} ${classes.status} ${classes.center}`}>A-Ready</TableCell>
                <TableCell className={`${classes.headerCell} ${classes.status} ${classes.center}`}>S-Ready</TableCell>
                <TableCell className={`${classes.headerCell} ${classes.status} ${classes.center}`}>H&amp;P</TableCell>
                <TableCell className={`${classes.headerCell} ${classes.event} ${classes.center}`}>Event</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {groups.map(group => (
                <React.Fragment key={group.key}>
                  <TableRow>
                    <TableCell colSpan={12} className={classes.groupCell}>
                      <span className={classes.groupName}>{group.name}</span>
                      <span className={classes.groupCount}>
                        {group.patients.length} {group.patients.length === 1 ? 'case' : 'cases'}
                      </span>
                    </TableCell>
                  </TableRow>
                  {group.patients.map(patient => {
                    const readyForOr = isReadyForOr(patient);
                    return (
                      <TableRow key={patient.id} hover>
                        <TextCell value={patient.physician?.name} className={`${classes.cell} ${classes.surgeon}`} />
                        <TextCell
                          value={patient.priority}
                          className={`${classes.cell} ${classes.order} ${classes.center}`}
                        />
                        <TextCell
                          value={patient.procedure?.startTimeText}
                          className={`${classes.cell} ${classes.time} ${classes.center}`}
                        />
                        <TextCell value={patient.name} className={`${classes.cell} ${classes.patient}`} />
                        <TextCell value={patient.bed} className={`${classes.cell} ${classes.bay} ${classes.center}`} />
                        <TextCell value={patient.preOpNurse?.name} className={`${classes.cell} ${classes.nurse}`} />
                        <TextCell value={patient.anesthesiologist?.name} className={`${classes.cell} ${classes.mda}`} />
                        <TextCell
                          value={patient.procedure?.anesthesiaType}
                          className={`${classes.cell} ${classes.anesthesia}`}
                        />
                        {renderStatusCell(patient, 'preOpBoardAnesthesiaReady')}
                        {renderStatusCell(patient, 'preOpBoardSurgeonReady')}
                        {renderStatusCell(patient, 'preOpBoardHistoryAndPhysicalReady')}
                        <TableCell className={`${classes.cell} ${classes.center}`}>
                          <Chip
                            size="small"
                            label={readyForOr ? 'Ready for OR' : 'PreOp'}
                            className={readyForOr ? classes.eventReady : classes.eventPreOp}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Box className={classes.footer}>
        <Troubleshooting roomName="PreOP Board" />
      </Box>

      <Menu
        anchorEl={menu?.anchor}
        open={Boolean(menu)}
        onClose={() => setMenu(null)}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        MenuListProps={{ 'aria-label': 'PreOP Board status options' }}
      >
        <MenuItem className={classes.menuItem} onClick={() => updateStatus(true)}>
          <CheckCircleRounded className={classes.readyMenuIcon} /> Ready
        </MenuItem>
        <MenuItem className={classes.menuItem} onClick={() => updateStatus(false)}>
          <CancelRounded className={classes.notReadyMenuIcon} /> Not ready
        </MenuItem>
        <MenuItem className={classes.menuItem} onClick={() => updateStatus(null)}>
          <RemoveCircleOutlineRounded className={classes.clearMenuIcon} /> Clear
        </MenuItem>
      </Menu>

      <Snackbar open={Boolean(failure)} autoHideDuration={5000} onClose={() => setFailure(null)}>
        <Alert severity="error" onClose={() => setFailure(null)}>
          {failure}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PreOpBoard;
